import type {
  AdminManifestMetadata,
  AdminReleaseAlignmentSummary,
  AdminReleaseArtifactStatus,
  AdminReleaseServiceRollout,
  AdminReleaseStatusPayload,
} from './release.types'

export const asString = (value: unknown): string =>
  typeof value === 'string' ? value : ''

const toGlobalStatus = (
  value: string,
): AdminReleaseAlignmentSummary['globalStatus'] => {
  const normalized = value.trim().toLowerCase()
  if (
    normalized === 'aligned' ||
    normalized === 'drift_detected' ||
    normalized === 'partial_data' ||
    normalized === 'unavailable'
  ) {
    return normalized
  }
  if (normalized === 'stable') return 'aligned'
  if (
    normalized === 'deploying' ||
    normalized === 'degraded' ||
    normalized === 'failed'
  ) {
    return 'drift_detected'
  }
  return 'unavailable'
}

export const toReleaseEnv = (value: unknown): 'dev' | 'prod' =>
  asString(value).trim().toLowerCase() === 'dev' ? 'dev' : 'prod'

const toArtifact = (value: unknown): AdminReleaseArtifactStatus | null => {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const releaseId = asString(raw.releaseId).trim()
  if (!releaseId) return null

  return {
    releaseId,
    manifestExists: raw.manifestExists === true,
    manifestPath: asString(raw.manifestPath).trim(),
    envExists: raw.envExists === true,
    envPath: asString(raw.envPath).trim(),
    notesExists: raw.notesExists === true,
    notesPath: asString(raw.notesPath).trim(),
  }
}

const toManifestMetadata = (value: unknown): AdminManifestMetadata | null => {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const servicesRaw =
    raw.services && typeof raw.services === 'object'
      ? (raw.services as Record<string, unknown>)
      : {}

  const services = Object.fromEntries(
    Object.entries(servicesRaw)
      .filter(([key]) => key.trim().length > 0)
      .map(([key, image]) => [key, asString(image).trim()]),
  )

  const rollbackSafeRaw =
    raw.dbChange && typeof raw.dbChange === 'object'
      ? (raw.dbChange as Record<string, unknown>).rollbackSafe
      : null
  const rollbackSafe = typeof rollbackSafeRaw === 'boolean' ? rollbackSafeRaw : null
  const dbChangeType =
    raw.dbChange && typeof raw.dbChange === 'object'
      ? asString((raw.dbChange as Record<string, unknown>).type).trim()
      : ''

  return {
    releaseId: asString(raw.releaseId).trim(),
    gitSha: asString(raw.gitSha).trim(),
    createdAt: asString(raw.createdAt).trim(),
    dbChange: {
      type: dbChangeType,
      rollbackSafe,
    },
    services,
  }
}

const toServiceRollout = (value: unknown): AdminReleaseServiceRollout | null => {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const serviceName = asString(raw.serviceName).trim()
  if (!serviceName) return null

  const healthRaw = asString(raw.health).toLowerCase()
  const alignmentRaw = asString(raw.alignmentStatus).toLowerCase()

  const health: AdminReleaseServiceRollout['health'] =
    healthRaw === 'ok' || healthRaw === 'degraded' || healthRaw === 'down'
      ? healthRaw
      : 'unknown'

  const alignmentStatus: AdminReleaseServiceRollout['alignmentStatus'] =
    alignmentRaw === 'aligned' ||
    alignmentRaw === 'version_mismatch' ||
    alignmentRaw === 'unhealthy' ||
    alignmentRaw === 'missing'
      ? alignmentRaw
      : 'unknown'

  return {
    serviceName,
    expectedReleaseId: asString(raw.expectedReleaseId).trim(),
    expectedImage: asString(raw.expectedImage).trim(),
    expectedTagOrVersion:
      asString(raw.expectedTagOrVersion).trim() ||
      asString(raw.expectedVersion).trim(),
    expectedVersion:
      asString(raw.expectedVersion).trim() ||
      asString(raw.expectedTagOrVersion).trim(),
    runningVersion: asString(raw.runningVersion).trim(),
    runningBuildRef: asString(raw.runningBuildRef).trim(),
    health,
    alignmentStatus,
    lastSeen: asString(raw.lastSeen).trim(),
  }
}

const toSummary = (value: unknown): AdminReleaseAlignmentSummary => {
  const fallback: AdminReleaseAlignmentSummary = {
    globalStatus: 'unavailable',
    status: 'unavailable',
    totalCount: 0,
    totalServices: 0,
    alignedCount: 0,
    mismatchCount: 0,
    versionMismatchCount: 0,
    unhealthyCount: 0,
    missingCount: 0,
    unknownCount: 0,
  }
  if (!value || typeof value !== 'object') return fallback

  const raw = value as Record<string, unknown>
  const status = toGlobalStatus(asString(raw.status))
  const globalStatusRaw = asString(raw.globalStatus).trim()
  const globalStatus = globalStatusRaw ? toGlobalStatus(globalStatusRaw) : status

  const toCount = (count: unknown): number =>
    typeof count === 'number' && Number.isFinite(count) && count >= 0
      ? Math.floor(count)
      : 0

  return {
    globalStatus,
    status,
    totalCount: toCount(raw.totalCount) || toCount(raw.totalServices),
    totalServices: toCount(raw.totalServices) || toCount(raw.totalCount),
    alignedCount: toCount(raw.alignedCount),
    mismatchCount: toCount(raw.mismatchCount) || toCount(raw.versionMismatchCount),
    versionMismatchCount:
      toCount(raw.versionMismatchCount) || toCount(raw.mismatchCount),
    unhealthyCount: toCount(raw.unhealthyCount),
    missingCount: toCount(raw.missingCount),
    unknownCount: toCount(raw.unknownCount),
  }
}

export const normalizeReleaseStatusPayload = (
  payload: Record<string, unknown>,
): AdminReleaseStatusPayload => {
  const artifactsRaw =
    payload.artifacts && typeof payload.artifacts === 'object'
      ? (payload.artifacts as Record<string, unknown>)
      : {}

  const servicesRaw = Array.isArray(payload.services) ? payload.services : []

  return {
    env: toReleaseEnv(payload.env),
    environment: toReleaseEnv(payload.environment ?? payload.env),
    currentReleaseId:
      asString(payload.currentReleaseId).trim() || asString(payload.current).trim(),
    previousReleaseId:
      asString(payload.previousReleaseId).trim() ||
      asString(payload.previous).trim(),
    failedReleaseId:
      asString(payload.failedReleaseId).trim() || asString(payload.failed).trim(),
    current:
      asString(payload.current).trim() || asString(payload.currentReleaseId).trim(),
    previous:
      asString(payload.previous).trim() ||
      asString(payload.previousReleaseId).trim(),
    failed:
      asString(payload.failed).trim() || asString(payload.failedReleaseId).trim(),
    manifestExists: payload.manifestExists === true,
    manifestPath: asString(payload.manifestPath).trim(),
    notesExists: payload.notesExists === true,
    notesPath: asString(payload.notesPath).trim(),
    releaseNotes: asString(payload.releaseNotes),
    notes: asString(payload.releaseNotes) || asString(payload.notes),
    manifestMetadata: toManifestMetadata(payload.manifestMetadata),
    artifacts: {
      current: toArtifact(artifactsRaw.current),
      previous: toArtifact(artifactsRaw.previous),
      failed: toArtifact(artifactsRaw.failed),
    },
    summary: toSummary(payload.summary),
    services: servicesRaw
      .map(toServiceRollout)
      .filter((item): item is AdminReleaseServiceRollout => item !== null),
    refreshedAt: asString(payload.refreshedAt).trim() || new Date().toISOString(),
  }
}
